import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { merge, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { InwardsService } from 'app/modules/admin/transactions/inwards/inwards.service';
import { Inward } from 'app/modules/admin/transactions/inwards/inwards.types';
import { InwardStatus, InwardStatusData } from 'app/core/common/enums/inward.status';
import { SignalRService } from 'app/core/common/services/signalr.service';

interface StatusBucket {
    status: InwardStatus;
    label: string;
    count: number;
}

@Component({
    selector: 'my-inwards',
    templateUrl: './my-inwards.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyInwardsComponent implements OnInit, OnDestroy {

    buckets: StatusBucket[] = [];
    currentIndex = 0;
    paused = false;

    private readonly rotateMs = 4000;
    private readonly pollMs = 5 * 60 * 1000;
    private _rotateSub?: Subscription;
    private _unsubscribeAll = new Subject<void>();

    constructor(
        private _inwardsService: InwardsService,
        private _signalRService: SignalRService,
        private _router: Router,
        private _cdr: ChangeDetectorRef,
    ) {}

    get current(): StatusBucket | null {
        return this.buckets.length ? this.buckets[this.currentIndex] : null;
    }

    get totalCount(): number {
        return this.buckets.reduce((s, b) => s + b.count, 0);
    }

    ngOnInit(): void {
        this._signalRService.startConnection();

        merge(
            timer(0, this.pollMs),
            this._signalRService.inwardUpdates$,
        )
            .pipe(
                debounceTime(400),
                switchMap(() => this._inwardsService.getPendingInwards()),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe(inwards => this.rebuildBuckets(inwards || []));

        this.startRotation();
    }

    ngOnDestroy(): void {
        this.stopRotation();
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    next(): void {
        if (!this.buckets.length) { return; }
        this.currentIndex = (this.currentIndex + 1) % this.buckets.length;
        this.restartRotation();
        this._cdr.markForCheck();
    }

    prev(): void {
        if (!this.buckets.length) { return; }
        this.currentIndex = (this.currentIndex - 1 + this.buckets.length) % this.buckets.length;
        this.restartRotation();
        this._cdr.markForCheck();
    }

    togglePause(): void {
        this.paused = !this.paused;
        if (this.paused) {
            this.stopRotation();
        } else {
            this.startRotation();
        }
        this._cdr.markForCheck();
    }

    openStatus(): void {
        const bucket = this.current;
        if (!bucket) { return; }
        this._router.navigate(['/transactions/inwards'], {
            queryParams: { inwardStatus: bucket.status },
        });
    }

    private rebuildBuckets(inwards: Inward[]): void {
        const map = new Map<InwardStatus, number>();
        for (const inward of inwards) {
            const key = inward.inwardStatus as InwardStatus;
            if (!key) { continue; }
            map.set(key, (map.get(key) || 0) + 1);
        }

        const order = [
            InwardStatus.Inward,
            InwardStatus.Survey,
            InwardStatus.Workout,
            InwardStatus.DocumentPending,
            InwardStatus.Checking,
            InwardStatus.Typing,
            InwardStatus.Outward,
            InwardStatus.Delivered,
            InwardStatus.Cancel,
        ];

        const next: StatusBucket[] = [];
        for (const status of order) {
            const count = map.get(status) || 0;
            if (count > 0) {
                next.push({ status, label: InwardStatusData[status], count });
            }
        }

        this.buckets = next;
        if (this.currentIndex >= this.buckets.length) {
            this.currentIndex = 0;
        }
        this._cdr.markForCheck();
    }

    private startRotation(): void {
        this.stopRotation();
        if (this.paused) { return; }
        this._rotateSub = timer(this.rotateMs, this.rotateMs).subscribe(() => {
            if (this.buckets.length > 1) {
                this.currentIndex = (this.currentIndex + 1) % this.buckets.length;
                this._cdr.markForCheck();
            }
        });
    }

    private stopRotation(): void {
        this._rotateSub?.unsubscribe();
        this._rotateSub = undefined;
    }

    private restartRotation(): void {
        if (!this.paused) { this.startRotation(); }
    }
}
